import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  message,
  Dropdown,
  Modal,
  Spin,
  Empty,
} from "antd";
import {
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  EditOutlined,
  MoreOutlined,
  SyncOutlined,
  CloudUploadOutlined,
  FileDoneOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { Switch, Tooltip } from "antd";
import {
  PageHeader,
  TimesheetContainer,
  MetricsSection,
  MetricCard,
  FiltersContainer,
  TabsContainer,
  TableContainer,
  Flex,
} from "./Timesheet.style";
import TimesheetDetailsModal from "../../Admin/Timesheet/TimesheetDetailsModal";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const getMonthName = (monthNum) => {
  const months = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];
  return months[monthNum - 1] || "";
};

const transformTimesheetData = (apiData) => {
  return apiData.map(ts => ({
    id: ts.id,
    talentId: ts.talentId,
    talentName: ts.talentName || ts.talentId,
    partner: ts.partnerOrganization,
    jobId: ts.jobId || "—",
    role: ts.role || "N/A",
    month: ts.monthName || getMonthName(ts.month),
    monthNum: ts.month,
    year: String(ts.year),
    actualWorkingDays: ts.totalWorkingDays,
    workingDays: ts.actualWorkingDays,
    billableHours: parseFloat(ts.billableHours) || 0,
    monthlyRate: parseFloat(ts.monthlyRate) || 0,
    clientRate: parseFloat(ts.clientRate) || parseFloat(ts.monthlyRate) || 0,
    calculatedAmount: parseFloat(ts.calculatedAmount) || 0,
    leaveTaken: { 
      count: (ts.paidLeaveCount || 0) + (ts.unpaidLeaveCount || 0), 
      dates: ts.leaveDates || [] 
    },
    paidLeave: ts.paidLeaveCount || 0,
    status: ts.status,
    timesheetFileUrl: ts.timesheetFileUrl,
    submittedDate: ts.submittedAt ? new Date(ts.submittedAt).toISOString().split('T')[0] : null,
    verifiedDate: ts.verifiedAt ? new Date(ts.verifiedAt).toISOString().split('T')[0] : null,
    approvedDate: ts.approvedAt ? new Date(ts.approvedAt).toISOString().split('T')[0] : null,
    rejectionReason: ts.rejectionReason,
    workingHoursPerDay: ts.workingHoursPerDay || 8,
  }));
};

const CustomerTimesheet = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Pending Review");
  const [searchText, setSearchText] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [modalMode, setModalMode] = useState("view");
  const [modifyModalVisible, setModifyModalVisible] = useState(false);
  const [modifyReason, setModifyReason] = useState("");

  const [timesheets, setTimesheets] = useState([]);
  const [myPartnersOnly, setMyPartnersOnly] = useState(
    () => sessionStorage.getItem("timesheet_myPartnersOnly") === "true"
  );
  const [activePartnerOrgs, setActivePartnerOrgs] = useState([]);
  const [partnerOrgsLoading, setPartnerOrgsLoading] = useState(false);

  const fetchActivePartnerOrgs = useCallback(async () => {
    setPartnerOrgsLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_PARTNERS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const relations = response.data.relations || [];
      const orgs = relations
        .filter((r) => r.status === "active" && r.partnerOrganization)
        .map((r) => r.partnerOrganization);
      setActivePartnerOrgs(orgs);
      return orgs;
    } catch (error) {
      console.error("Error fetching partner orgs:", error);
      message.error("Failed to load partner organizations");
      return [];
    } finally {
      setPartnerOrgsLoading(false);
    }
  }, []);

  const fetchTimesheets = useCallback(async (partnerOrgs = []) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const body = {};
      if (filterYear) body.year = parseInt(filterYear);
      if (filterMonth) {
        const monthMap = {
          "January": 1, "February": 2, "March": 3, "April": 4, "May": 5, "June": 6,
          "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12
        };
        body.month = monthMap[filterMonth];
      }
      if (partnerOrgs.length > 0) {
        body.partnerOrganizations = partnerOrgs;
      }
      
      const response = await axios.post(API_CONST.GET_TIMESHEETS, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.timesheets) {
        const transformed = transformTimesheetData(response.data.timesheets);
        setTimesheets(transformed);
      }
    } catch (error) {
      console.error("Error fetching timesheets:", error);
      message.error("Failed to load timesheets");
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth]);

  const handleMyPartnersToggle = async (checked) => {
    setMyPartnersOnly(checked);
    sessionStorage.setItem("timesheet_myPartnersOnly", String(checked));
    if (checked) {
      if (activePartnerOrgs.length === 0) {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setTimesheets([]);
        } else {
          fetchTimesheets(orgs);
        }
      } else {
        fetchTimesheets(activePartnerOrgs);
      }
    } else {
      fetchTimesheets([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (sessionStorage.getItem("timesheet_myPartnersOnly") === "true") {
        const orgs = await fetchActivePartnerOrgs();
        if (orgs.length === 0) {
          setTimesheets([]);
        } else {
          fetchTimesheets(orgs);
        }
      } else {
        fetchTimesheets([]);
      }
    };
    init();
  }, [fetchActivePartnerOrgs, fetchTimesheets]);

  const handleView = (record) => {
    setSelectedTimesheet(record);
    setModalMode("view");
    setViewModalVisible(true);
  };

  const handleApprove = (record) => {
    Modal.confirm({
      title: "Approve Timesheet",
      content: `Are you sure you want to approve the timesheet for ${record.talentName} for ${record.month} ${record.year}?`,
      okText: "Approve",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          const response = await axios.put(`${API_CONST.APPROVE_TIMESHEET}/${record.id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.timesheet) {
            message.success(`Timesheet for ${record.talentName} approved successfully`);
            fetchTimesheets();
            
            if (selectedTimesheet && selectedTimesheet.id === record.id) {
              setViewModalVisible(false);
              setSelectedTimesheet(null);
            }
          }
        } catch (error) {
          console.error("Error approving timesheet:", error);
          message.error(error.response?.data?.message || "Failed to approve timesheet");
        }
      },
    });
  };

  const handleAskToModify = (record) => {
    setSelectedTimesheet(record);
    setModifyReason("");
    setModifyModalVisible(true);
  };

  const handleSubmitModifyRequest = async () => {
    if (!modifyReason.trim()) {
      message.warning("Please enter a reason for modification");
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.put(`${API_CONST.REJECT_TIMESHEET}/${selectedTimesheet.id}`, {
        rejectionReason: modifyReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.timesheet) {
        message.success(`Modification request sent to partner for ${selectedTimesheet.talentName}`);
        fetchTimesheets();
        
        if (viewModalVisible && selectedTimesheet) {
          setViewModalVisible(false);
        }
        
        setModifyModalVisible(false);
        setModifyReason("");
        setSelectedTimesheet(null);
      }
    } catch (error) {
      console.error("Error requesting modification:", error);
      message.error(error.response?.data?.message || "Failed to send modification request");
    }
  };

  const handleCancelModifyModal = () => {
    setModifyModalVisible(false);
    setModifyReason("");
    setSelectedTimesheet(null);
  };

  const handleDownload = async (record) => {
    if (!record.timesheetFileUrl) {
      message.warning("No timesheet file available for download");
      return;
    }
    
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(API_CONST.GET_TIMESHEET_DOWNLOAD_URL, {
        timesheetFileUrl: record.timesheetFileUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error("Error getting download URL:", error);
      message.error("Failed to download timesheet");
    }
  };

  const handleCloseViewModal = () => {
    setViewModalVisible(false);
    setSelectedTimesheet(null);
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem("authToken");
      const now = new Date();
      const response = await axios.post(API_CONST.SYNC_NOW, {
        month: now.getMonth() + 1,
        year: now.getFullYear()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { timesheets: tsResult, invoices: invResult } = response.data;
      message.success(`Sync complete: ${tsResult.generated} timesheets generated, ${invResult.generated} invoices created`);
      fetchTimesheets();
    } catch (error) {
      console.error("Error syncing:", error);
      message.error(error.response?.data?.message || "Failed to sync");
    } finally {
      setSyncing(false);
    }
  };

  const getFilteredData = () => {
    let filtered = [...timesheets];

    if (activeTab === "Pending Review") {
      filtered = filtered.filter((t) => t.status === "verified");
    } else if (activeTab === "Approved") {
      filtered = filtered.filter((t) => t.status === "approved");
    }

    if (searchText) {
      filtered = filtered.filter(
        (t) =>
          t.talentName.toLowerCase().includes(searchText.toLowerCase()) ||
          t.talentId.toLowerCase().includes(searchText.toLowerCase()) ||
          t.role.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return filtered;
  };

  const getMetrics = () => {
    const pendingReview = timesheets.filter((t) => t.status === "verified").length;
    const approved = timesheets.filter((t) => t.status === "approved").length;
    const totalAmountPending = timesheets
      .filter((t) => t.status === "verified")
      .reduce((sum, t) => sum + t.calculatedAmount, 0);
    const totalAmountApproved = timesheets
      .filter((t) => t.status === "approved")
      .reduce((sum, t) => sum + t.calculatedAmount, 0);

    return { pendingReview, approved, totalAmountPending, totalAmountApproved };
  };

  const metrics = getMetrics();

  const getActionMenuItems = (record) => {
    const items = [
      {
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => handleView(record),
      },
      {
        key: "download",
        label: "Download",
        icon: <DownloadOutlined />,
        onClick: () => handleDownload(record),
      },
    ];

    if (record.status === "verified") {
      items.unshift(
        {
          key: "approve",
          label: "Approve",
          icon: <CheckCircleOutlined />,
          onClick: () => handleApprove(record),
        },
        {
          key: "modify",
          label: "Ask to Modify",
          icon: <EditOutlined />,
          onClick: () => handleAskToModify(record),
        }
      );
    }

    return items;
  };

  const columns = [
    {
      title: "Talent ID",
      dataIndex: "talentId",
      key: "talentId",
      width: 100,
    },
    {
      title: "Talent Name",
      dataIndex: "talentName",
      key: "talentName",
      width: 150,
      render: (name, record) => record.talentId ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/customer/talent-details/${record.talentId}`)}
        >
          {name}
        </a>
      ) : name,
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 160,
      render: (id) => <span style={{ color: "#014c75", fontWeight: 500 }}>{id || "—"}</span>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
    },
    {
      title: "Month",
      key: "month",
      width: 130,
      render: (_, record) => (
        <span>
          {record.month} {record.year}
          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
            ({record.actualWorkingDays} days)
          </div>
        </span>
      ),
    },
    {
      title: "Leave Taken",
      key: "leaveTaken",
      width: 110,
      render: (_, record) => {
        const leaveTaken = record.leaveTaken;
        const count = leaveTaken?.count || 0;
        const dates = leaveTaken?.dates || [];
        return (
          <div>
            <div style={{ fontWeight: 500 }}>{count} days</div>
            {count > 0 && dates.length > 0 && (
              <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                {dates.slice(0, 2).map(d => parseInt(d.split('-')[2], 10)).join(', ')}
                {dates.length > 2 && '...'}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Working Days",
      dataIndex: "workingDays",
      key: "workingDays",
      width: 120,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Billable Hours",
      dataIndex: "billableHours",
      key: "billableHours",
      width: 120,
      align: /** @type {'center'} */ ("center"),
    },
    {
      title: "Monthly Cost",
      dataIndex: "calculatedAmount",
      key: "calculatedAmount",
      width: 150,
      align: /** @type {'right'} */ ('right'),
      render: (amount) => (
        <span style={{ fontWeight: 600, color: "#00d9a9", whiteSpace: "nowrap" }}>
          ₹ {Math.round(amount || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status) => {
        const config = {
          verified: { color: "blue", text: "Pending Approval" },
          approved: { color: "green", text: "Approved" },
          rejected: { color: "orange", text: "Modification Requested" },
        };
        const { color, text } = config[status] || { color: "default", text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: "Submitted Date",
      dataIndex: "submittedDate",
      key: "submittedDate",
      width: 130,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      fixed: /** @type {'right'} */ ("right"),
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];

  const renderFilters = () => (
    <FiltersContainer>
      <Flex style={{ gap: "12px", flexWrap: "wrap" }}>
        <Input
          placeholder="Search by Talent Name, ID, or Role"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="Select Month"
          value={filterMonth || undefined}
          onChange={setFilterMonth}
          style={{ width: 150 }}
          allowClear
        >
          <Option value="January">January</Option>
          <Option value="February">February</Option>
          <Option value="March">March</Option>
          <Option value="April">April</Option>
          <Option value="May">May</Option>
          <Option value="June">June</Option>
          <Option value="July">July</Option>
          <Option value="August">August</Option>
          <Option value="September">September</Option>
          <Option value="October">October</Option>
          <Option value="November">November</Option>
          <Option value="December">December</Option>
        </Select>
        <Select
          placeholder="Select Year"
          value={filterYear || undefined}
          onChange={setFilterYear}
          style={{ width: 120 }}
          allowClear
        >
          {yearOptions.map(year => (
            <Option key={year} value={String(year)}>{year}</Option>
          ))}
        </Select>
        <Tooltip title="Show only timesheets from your registered partner network">
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: myPartnersOnly ? "#e6f7ff" : "#fafafa",
            border: `1px solid ${myPartnersOnly ? "#91d5ff" : "#d9d9d9"}`,
            borderRadius: 6,
            padding: "4px 12px",
            cursor: "pointer",
          }}>
            <TeamOutlined style={{ color: myPartnersOnly ? "#1890ff" : "#999" }} />
            <span style={{ fontSize: 13, color: myPartnersOnly ? "#1890ff" : "#595959", userSelect: "none" }}>
              My Partners Only
            </span>
            <Switch
              size="small"
              checked={myPartnersOnly}
              onChange={handleMyPartnersToggle}
              loading={partnerOrgsLoading}
            />
          </div>
        </Tooltip>
      </Flex>
    </FiltersContainer>
  );

  return (
    <TimesheetContainer>
      <PageHeader>
        <h1>Timesheet</h1>
        <p>Review and manage timesheets for your hired talents</p>
      </PageHeader>

      <MetricsSection>
        <MetricCard>
          <div className="metric-label">Pending Review</div>
          <div className="metric-value">{metrics.pendingReview}</div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Approved</div>
          <div className="metric-value">{metrics.approved}</div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Total Amount Pending</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountPending / 100000).toFixed(2)}L
          </div>
        </MetricCard>
        <MetricCard>
          <div className="metric-label">Total Amount Approved</div>
          <div className="metric-value">
            ₹ {(metrics.totalAmountApproved / 100000).toFixed(2)}L
          </div>
        </MetricCard>
      </MetricsSection>

      <TabsContainer>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            className={`tab-button ${activeTab === "Pending Review" ? "active" : ""}`}
            icon={<CloudUploadOutlined />}
            onClick={() => setActiveTab("Pending Review")}
          >
            Pending Review ({metrics.pendingReview})
          </Button>
          <Button
            className={`tab-button ${activeTab === "Approved" ? "active" : ""}`}
            icon={<FileDoneOutlined />}
            onClick={() => setActiveTab("Approved")}
          >
            Approved ({metrics.approved})
          </Button>
        </div>
        <Button
          type="link"
          icon={<SyncOutlined spin={syncing} />}
          onClick={handleSyncNow}
          loading={syncing}
          style={{ padding: 0, color: '#1890ff' }}
        >
          Sync Now
        </Button>
      </TabsContainer>

      {renderFilters()}

      <TableContainer>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : getFilteredData().length > 0 ? (
          <Table
            columns={columns}
            dataSource={getFilteredData()}
            rowKey="id"
            pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
              pageSize: 10,
              showTotal: (total) => `Total ${total} timesheets`,
            }}
            scroll={{ x: 1400 }}
          />
        ) : (
          <Empty description="No timesheets found" style={{ padding: '50px' }} />
        )}
      </TableContainer>

      <TimesheetDetailsModal
        visible={viewModalVisible}
        onClose={handleCloseViewModal}
        timesheet={selectedTimesheet}
        mode="view"
        userRole="Customer"
        onSubmit={() => {}}
      />

      <Modal
        title={`Request Modification - ${selectedTimesheet?.talentName || ""}`}
        open={modifyModalVisible}
        onOk={handleSubmitModifyRequest}
        onCancel={handleCancelModifyModal}
        width={600}
        okText="Send Request"
        okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
      >
        {selectedTimesheet && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
              <div><strong>Talent ID:</strong> {selectedTimesheet.talentId}</div>
              <div><strong>Talent Name:</strong> {selectedTimesheet.talentName}</div>
              <div><strong>Role:</strong> {selectedTimesheet.role}</div>
              <div><strong>Month:</strong> {selectedTimesheet.month} {selectedTimesheet.year}</div>
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#014c75", fontWeight: 500 }}>
                Reason for Modification *
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Please specify what needs to be modified and why..."
                value={modifyReason}
                onChange={(e) => setModifyReason(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>
    </TimesheetContainer>
  );
};

export default CustomerTimesheet;
